import React from 'react';
import { format, formatDistanceToNow, isSameDay } from 'date-fns';
import type { AuditEventEnvelope } from '@/types';
import { STATUS_CHANGED_EVENT, actorDisplayName, burstPhrase, categoryMeta, isAlertRow } from './auditMeta';
import ActorChip from './ActorChip';
import AuditEventRow from './AuditEventRow';

interface AuditBurstRowProps {
  /** The burst's events in list order (newest first). */
  events: AuditEventEnvelope[];
  expanded: boolean;
  onToggle: () => void;
  selectedId: string | null;
  onSelect: (event: AuditEventEnvelope) => void;
  onCopyIp?: (ip: string) => void;
  onDrillDeposit?: (event: AuditEventEnvelope) => void;
}

/**
 * A collapsed run of rapid, identical-shape events (same person + event type + client) —
 * e.g. "Azia Chrest deleted 43 deposits" with its time range. The chevron (or the row)
 * expands it to the individual event rows.
 */
const AuditBurstRow: React.FC<AuditBurstRowProps> = ({
  events,
  expanded,
  onToggle,
  selectedId,
  onSelect,
  onCopyIp,
  onDrillDeposit,
}) => {
  const first = events[0];
  const newest = new Date(first.ts);
  const oldest = new Date(events[events.length - 1].ts);
  const cat = categoryMeta(first.category);
  const warn = isAlertRow(first);
  const phrase = burstPhrase(events);
  const actor = actorDisplayName(first);
  const ips = new Set(events.map((e) => e.ipAddress || ''));
  const ip = ips.size === 1 ? first.ipAddress : null;
  const containsSelected = !!selectedId && events.some((e) => e.id === selectedId);
  const rangeText = `${format(oldest, 'MMM d, h:mm a')} – ${format(newest, isSameDay(oldest, newest) ? 'h:mm a' : 'MMM d, h:mm a')}`;
  const toggleLabel = `${expanded ? 'Collapse' : 'Expand'} ${events.length} events: ${actor} ${phrase}`;

  return (
    <>
      <tr
        className={`cursor-pointer hover:bg-blue-50/40 ${
          containsSelected && !expanded ? 'bg-blue-50' : warn ? 'bg-red-50/40' : 'bg-gray-50/60'
        }`}
        onClick={onToggle}
      >
        <td className="whitespace-nowrap px-3 py-2 align-top text-gray-500" title={`${oldest.toISOString()} – ${newest.toISOString()}`}>
          <div>{formatDistanceToNow(newest, { addSuffix: true })}</div>
          <div className="text-[10px] text-gray-400">{rangeText}</div>
        </td>
        <td className="px-3 py-2 align-top">
          <div className="flex items-start gap-2">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onToggle();
              }}
              aria-expanded={expanded}
              aria-label={toggleLabel}
              title={toggleLabel}
              className="mt-0.5 inline-flex h-5 w-4 shrink-0 items-center justify-center text-gray-400 hover:text-gray-700"
            >
              <i className={`fas fa-chevron-right text-[10px] transition-transform ${expanded ? 'rotate-90' : ''}`} aria-hidden="true" />
            </button>
            <span className={`mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded ${cat.bg}`}>
              <i className={`fas ${cat.icon} ${cat.color} text-[10px]`} aria-hidden="true" />
            </span>
            <span className={`leading-snug ${warn ? 'text-red-700' : 'text-gray-700'}`}>
              <span className="font-semibold text-gray-900">{actor}</span>
              <span> {phrase}</span>
              <span className="ml-1.5 rounded bg-gray-200 px-1 py-0.5 text-[9px] font-medium text-gray-600">
                {events.length.toLocaleString()} {first.eventType === STATUS_CHANGED_EVENT ? 'changes' : 'events'}
              </span>
            </span>
          </div>
        </td>
        <td className="px-3 py-2 align-top">
          <ActorChip event={first} />
        </td>
        <td className="whitespace-nowrap px-3 py-2 align-top font-mono text-[10px] text-gray-400">
          {ip || (ips.size > 1 ? 'multiple' : '—')}
        </td>
      </tr>
      {expanded &&
        events.map((event) => (
          <AuditEventRow
            key={event.id}
            event={event}
            nested
            selected={selectedId === event.id}
            onSelect={onSelect}
            onCopyIp={onCopyIp}
            onDrillDeposit={onDrillDeposit}
          />
        ))}
    </>
  );
};

export default AuditBurstRow;
